// Use the vendor's browser bundle; Webpack 5 no longer supplies Node core polyfills.
import gifFrames from 'gif-frames/dist/gif-frames';
import { GifReader } from 'omggif';
import { normalizeDelay } from './Timeline';

export const VALID_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];
export const MAX_FILE_SIZE = 10485760; // 10MB

export const validateFileIsImage = (file) => {
  if (!file || !file.name) return false;
  const fileExtension = file.name.split('.').pop();
  const isValidExtension = VALID_FILE_EXTENSIONS.includes(fileExtension.toLowerCase());

  // Also check MIME type if available to prevent file spoofing
  // Browsers usually populate 'type', but if it's missing, we fall back to extension only to avoid blocking valid files in edge cases
  let isValidMime = true;
  if (file.type) {
    isValidMime = file.type.startsWith('image/');
  }

  return isValidExtension && isValidMime;
};

export const validateFileSize = (file) => {
  return file.size <= MAX_FILE_SIZE;
};

export const getUniqueFileName = (existingNames, fileName) => {
  const namesSet = new Set(existingNames);
  if (!namesSet.has(fileName)) {
    return fileName;
  }

  const parts = fileName.split('.');
  const extension = parts.pop();
  const baseName = parts[0];

  let counter = 1;
  let newName = `${baseName} (${counter}).${extension}`;
  while (namesSet.has(newName)) {
    counter++;
    newName = `${baseName} (${counter}).${extension}`;
  }
  return newName;
};

export const processImageFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageSrc = reader.result;

      // Load as gif
      if (file.name.split('.').pop().toLowerCase() === 'gif') {
        processGif(imageSrc)
          .then(resolve)
          .catch(() => reject(new Error('Could not split gif into frames.')));
      } else {
        processStaticImage(imageSrc)
          .then(resolve)
          .catch(() => reject(new Error('Could not load image.')));
      }
    };
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
};

const createGifFrame = (frameData) => {
  return new Promise((resolve, reject) => {
    const canvas = frameData.getImage();
    const image = new Image();
    image.onload = () =>
      resolve({
        image,
        url: image.src,
        delay: normalizeDelay(frameData.frameInfo.delay * 10),
        // gif-frames returns a full logical-screen image, even for partial frames.
        width: image.width,
        height: image.height,
        x: 0, // Relative to image position
        y: 0,
      });
    image.onerror = () => reject(frameData);
    image.src = canvas.toDataURL();
  });
};

const processGif = (imageSrc) => {
  return gifFrames({
    url: imageSrc,
    frames: 'all',
    outputType: 'canvas',
    cumulative: false,
  }).then((frameData) => {
    return Promise.all(frameData.map(createGifFrame)).then(frames => {
      // Preserve the array-returning loader API; App promotes this metadata to
      // the image, where frame edits, effects and undo/redo preserve it.
      const bytes = Uint8Array.from(atob(imageSrc.split(',')[1]), char => char.charCodeAt(0));
      const repeats = new GifReader(bytes).loopCount();
      frames.playCount = repeats === null ? 1 : repeats === 0 ? 0 : repeats + 1;
      return frames;
    });
  });
};

const processStaticImage = (imageSrc) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve([{
        image,
        url: image.src,
        delay: 100,
        width: image.width,
        height: image.height,
        x: 0,
        y: 0,
      }]);
    };
    image.onerror = () => reject(image);
    image.src = imageSrc;
  });
};
